import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, ChevronDown, ChevronRight, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  getComments,
  createComment,
  updateComment,
  deleteComment,
  type Comment,
} from "@/lib/commentsStore";

interface Props {
  projectId: string;
  entityType: string;
  entityId: string;
  currentUserId?: string;
  myRole?: "owner" | "editor" | "viewer";
  className?: string;
}

export default function CommentThread({
  projectId,
  entityType,
  entityId,
  currentUserId,
  myRole,
  className,
}: Props) {
  const [open, setOpen] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);

  const canResolveOrModerate = myRole === "owner" || myRole === "editor";

  const load = async () => {
    try {
      const data = await getComments(projectId, entityType, entityId);
      setComments(data);
    } catch {
      toast.error("Failed to load comments");
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    if (open && !loaded) load();
  }, [open]);

  const handlePost = async () => {
    if (!draft.trim()) return;
    setPosting(true);
    try {
      const comment = await createComment(projectId, entityType, entityId, draft.trim());
      setComments((prev) => [...prev, comment]);
      setDraft("");
    } catch {
      toast.error("Failed to post comment");
    } finally {
      setPosting(false);
    }
  };

  const handleToggleResolved = async (comment: Comment) => {
    try {
      const updated = await updateComment(projectId, comment.id, { resolved: !comment.resolved });
      setComments((prev) => prev.map((c) => (c.id === comment.id ? updated : c)));
    } catch {
      toast.error("Failed to update comment");
    }
  };

  const handleDelete = async (commentId: string) => {
    try {
      await deleteComment(projectId, commentId);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch {
      toast.error("Failed to delete comment");
    }
  };

  const openCount = comments.filter((c) => !c.resolved).length;

  return (
    <div className={cn("border border-border rounded-lg overflow-hidden", className)}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground hover:bg-muted/50 transition-colors"
      >
        <MessageSquare className="h-3.5 w-3.5" />
        <span>{comments.length > 0 ? `${comments.length} comment${comments.length !== 1 ? "s" : ""}` : "Add comment"}</span>
        {openCount > 0 && <span className="text-[10px] text-amber-600">· {openCount} open</span>}
        {open ? <ChevronDown className="h-3 w-3 ml-auto" /> : <ChevronRight className="h-3 w-3 ml-auto" />}
      </button>

      {open && (
        <div className="border-t border-border p-3 space-y-3 bg-secondary/10">
          {comments.length > 0 && (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {comments.map((c) => (
                <div
                  key={c.id}
                  className={cn(
                    "rounded-md border border-border px-2.5 py-2 bg-card",
                    c.resolved && "opacity-50"
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] font-semibold text-card-foreground">{c.author_name || "Someone"}</span>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className="text-[10px] text-muted-foreground">
                        {new Date(c.created_at).toLocaleDateString()}
                      </span>
                      {canResolveOrModerate && (
                        <button
                          onClick={() => handleToggleResolved(c)}
                          className="text-muted-foreground hover:text-foreground transition-colors"
                          title={c.resolved ? "Mark unresolved" : "Mark resolved"}
                        >
                          <Check className="h-3 w-3" />
                        </button>
                      )}
                      {(c.author_id === currentUserId || canResolveOrModerate) && (
                        <button
                          onClick={() => handleDelete(c.id)}
                          className="text-muted-foreground hover:text-destructive transition-colors"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-foreground mt-1 whitespace-pre-wrap">{c.body}</p>
                </div>
              ))}
            </div>
          )}

          {myRole !== undefined && (
            <div className="flex gap-2">
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Add your judgment, context, or a question…"
                rows={2}
                className="flex-1 text-xs resize-none"
              />
              <Button size="sm" onClick={handlePost} disabled={!draft.trim() || posting} className="h-auto text-xs">
                Post
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
